import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import * as LucideIcons from "lucide-react";

const Footer = () => {
    const currentYear = new Date()?.getFullYear();

    const footerSections = [
        {
            title: 'Platform',
            links: [
                { name: 'Citizen Portal', href: '#citizen-portal' },
                { name: 'Revenue Collector App', href: '#collector-app' },
                { name: 'Assembly Dashboard', href: '#dashboard' },
                { name: 'Payment Gateway', href: '#payment-gateway' },
                { name: 'Analytics Suite', href: '#analytics' }
            ]
        },
        {
            title: 'Resources',
            links: [
                { name: 'Implementation Guide', href: '#implementation' },
                { name: 'Training Materials', href: '#training' },
                { name: 'API Documentation', href: '#api-docs' },
                { name: 'Success Stories', href: '#testimonials' },
                { name: 'Support Center', href: '#support' }
            ]
        },
        {
            title: 'Compliance',
            links: [
                { name: 'Ghana Data Protection Act', href: '#data-protection' },
                { name: 'Security Certifications', href: '#security' },
                { name: 'Government Integration', href: '#gov-integration' },
                { name: 'Audit & Reporting', href: '#audit' },
                { name: 'Privacy Policy', href: '#privacy' }
            ]
        },
        {
            title: 'Support',
            links: [
                { name: 'Contact Us', href: '#contact' },
                { name: 'Request Demo', href: '#demo-request' },
                { name: 'Technical Support', href: '#tech-support' },
                { name: 'Training Programs', href: '#training-programs' },
                { name: 'System Status', href: '#status' }
            ]
        }
    ];

    const governmentPartners = [
        { name: 'Ministry of Local Government', logo: 'Building2' },
        { name: 'Ghana.gov Portal', logo: 'Globe' },
        { name: 'Controller & Accountant General', logo: 'FileText' },
        { name: 'Bank of Ghana', logo: 'Landmark' }
    ];

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <footer className="bg-slate-900 text-white">
            {/* Main Footer Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
                    {/* Company Info */}
                    <div className="lg:col-span-1 space-y-6">
                        <div className="flex items-center space-x-3">
                            <div className="flex items-center justify-center w-12 h-12 bg-primary rounded-lg">
                                <Icon name="Building2" size={24} color="white" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold">Civicrate</h3>
                                <p className="text-sm text-slate-400">Digital Revenue Platform</p>
                            </div>
                        </div>

                        <p className="text-slate-300 text-sm leading-relaxed">
                            Transforming Ghana's local assemblies through secure, transparent,
                            and efficient digital revenue collection solutions.
                        </p>

                        {/* Government Integration Badge */}
                        <div className="inline-flex items-center space-x-2 px-3 py-2 bg-success/20 rounded-lg border border-success/30">
                            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                            <span className="text-sm font-medium text-success">Ghana.gov Certified</span>
                        </div>

                        {/* Social Links */}
                        <div className="flex space-x-4">
                            <button className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-slate-700 transition-colors">
                                <Icon name="Linkedin" size={20} color="white" />
                            </button>
                            <button className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-slate-700 transition-colors">
                                <Icon name="Twitter" size={20} color="white" />
                            </button>
                            <button className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-slate-700 transition-colors">
                                <Icon name="Facebook" size={20} color="white" />
                            </button>
                        </div>
                    </div>

                    {/* Footer Links */}
                    {footerSections?.map((section, index) => (
                        <div key={index} className="space-y-4">
                            <h4 className="text-lg font-semibold text-white">{section?.title}</h4>
                            <ul className="space-y-3">
                                {section?.links?.map((link, linkIndex) => (
                                    <li key={linkIndex}>
                                        <a
                                            href={link?.href}
                                            className="text-slate-300 hover:text-white transition-colors text-sm"
                                        >
                                            {link?.name}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Government Partners */}
                <div className="mt-16 pt-8 border-t border-slate-800">
                    <h4 className="text-lg font-semibold text-center text-white mb-8">
                        Government Partners & Integrations
                    </h4>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {governmentPartners?.map((partner, index) => (
                            <div key={index} className="flex items-center justify-center space-x-3 p-4 bg-slate-800/50 rounded-lg">
                                <Icon name={partner?.logo as keyof typeof LucideIcons} size={20} color="var(--color-slate-400)" />
                                <span className="text-sm text-slate-400 text-center">{partner?.name}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Contact Information */}
                <div className="mt-16 pt-8 border-t border-slate-800">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center md:text-left">
                            <h5 className="font-semibold text-white mb-3">Headquarters</h5>
                            <div className="space-y-2 text-sm text-slate-300">
                                <div className="flex items-center justify-center md:justify-start space-x-2">
                                    <Icon name="MapPin" size={16} />
                                    <span>Accra Digital Centre, Ghana</span>
                                </div>
                                <div className="flex items-center justify-center md:justify-start space-x-2">
                                    <Icon name="Phone" size={16} />
                                    <span>+233 XX XXX XXXX</span>
                                </div>
                                <div className="flex items-center justify-center md:justify-start space-x-2">
                                    <Icon name="Mail" size={16} />
                                    <span>info@civicrate.gov.gh</span>
                                </div>
                            </div>
                        </div>

                        <div className="text-center">
                            <h5 className="font-semibold text-white mb-3">Support Hours</h5>
                            <div className="space-y-2 text-sm text-slate-300">
                                <div>Monday - Friday: 8:00 AM - 6:00 PM</div>
                                <div>Saturday: 9:00 AM - 2:00 PM</div>
                                <div>Emergency Support: 24/7</div>
                            </div>
                        </div>

                        <div className="text-center md:text-right">
                            <h5 className="font-semibold text-white mb-3">Quick Actions</h5>
                            <div className="space-y-3">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    iconName="Calendar"
                                    iconPosition="left"
                                    className="border-slate-600 text-slate-300 hover:bg-slate-800"
                                >
                                    Schedule Demo
                                </Button>
                                <div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        iconName="Phone"
                                        iconPosition="left"
                                        className="text-slate-300 hover:bg-slate-800"
                                    >
                                        Emergency Support
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Bottom Bar */}
            <div className="border-t border-slate-800 bg-slate-950">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
                        <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6 text-sm text-slate-400">
                            <span>© {currentYear} Civicrate. All rights reserved.</span>
                            <div className="flex items-center space-x-4">
                                <a href="#privacy" className="hover:text-white transition-colors">Privacy Policy</a>
                                <a href="#terms" className="hover:text-white transition-colors">Terms of Service</a>
                                <a href="#cookies" className="hover:text-white transition-colors">Cookie Policy</a>
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            {/* Security Badges */}
                            <div className="flex items-center space-x-2 text-xs text-slate-400">
                                <Icon name="Shield" size={16} color="var(--color-success)" />
                                <span>ISO 27001 Certified</span>
                            </div>

                            <div className="flex items-center space-x-2 text-xs text-slate-400">
                                <Icon name="Lock" size={16} color="var(--color-success)" />
                                <span>256-bit SSL</span>
                            </div>

                            {/* Back to Top */}
                            <button
                                onClick={scrollToTop}
                                className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-slate-700 transition-colors"
                                aria-label="Back to top"
                            >
                                <Icon name="ArrowUp" size={20} color="white" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;